import { FormHelperText, Stack, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import {
    CandidateFilterCategory,
    SearchIndexField,
} from '__generated__/graphql'
import type { DateRangeFacetValue } from 'shared/types/searchTypes'
import { useEffect, useState } from 'react'
import { useRosterSearchFilter } from 'recruiter/hooks'
import { getPxAsRem } from 'shared/utils'

const now = new Date()
const RANGE_ERROR_MESSAGE = '"From" date must be earlier than "To" date'

export function RosterCreationDateFilter() {
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterCreatedAt
    )
    const [rangeError, setRangeError] = useState<string | null>(null)

    const dateRangeInFilter = value?.value?.dateRange ?? {
        gte: null,
        lte: null,
    }

    const [fromDate, setFromDate] = useState<Date | null>(
        dateRangeInFilter.gte ? new Date(dateRangeInFilter.gte) : null
    )

    const [toDate, setToDate] = useState<Date | null>(
        dateRangeInFilter.lte ? new Date(dateRangeInFilter.lte) : null
    )

    useEffect(() => {
        if (!dateRangeInFilter.gte) setFromDate(null)
        if (!dateRangeInFilter.lte) setToDate(null)
    }, [dateRangeInFilter.gte, dateRangeInFilter.lte])

    const handleDateChange = (
        rangeKey: keyof DateRangeFacetValue,
        newDate: Date | null
    ) => {
        if (rangeKey === 'gte') {
            setFromDate(newDate)
            if (toDate && newDate && newDate > toDate) {
                setRangeError(RANGE_ERROR_MESSAGE)
                return
            }
        } else {
            setToDate(newDate)
            if (fromDate && newDate && newDate < fromDate) {
                setRangeError(RANGE_ERROR_MESSAGE)
                return
            }
        }

        setRangeError(null)
        const dateRange = {
            gte: fromDate?.toISOString() ?? null,
            lte: toDate?.toISOString() ?? null,
        }

        try {
            dateRange[rangeKey] = newDate?.toISOString() ?? null
        } catch {
            dateRange[rangeKey] = null
            if (rangeKey === 'gte') {
                setFromDate(null)
            } else {
                setToDate(null)
            }
        }

        if (dateRange.gte === null && dateRange.lte === null) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { dateRange },
        })
    }

    return (
        <Stack gap={1}>
            <Typography fontSize="0.875rem" color="text.secondary">
                Creation Date
            </Typography>
            <Stack gap={getPxAsRem(5)} direction="row">
                <DatePicker
                    label="From"
                    onChange={(date) => {
                        handleDateChange('gte', date)
                    }}
                    format="MM/yyyy"
                    sx={{ width: getPxAsRem(200) }}
                    value={fromDate}
                    maxDate={now}
                    disableOpenPicker
                    slotProps={{
                        textField: { error: Boolean(rangeError) },
                    }}
                />
                <DatePicker
                    label="To"
                    onChange={(date) => {
                        handleDateChange('lte', date)
                    }}
                    format="MM/yyyy"
                    sx={{ width: getPxAsRem(200) }}
                    value={toDate}
                    maxDate={now}
                    slotProps={{
                        textField: { error: Boolean(rangeError) },
                    }}
                    disableOpenPicker
                />
            </Stack>

            {rangeError && <FormHelperText error>{rangeError}</FormHelperText>}
        </Stack>
    )
}
